import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { ThemeProvider } from './context/ThemeContext'
import PageLayout from './components/layout/PageLayout'
import Landing from './pages/Landing'
import Dashboard from './pages/Dashboard'
import CaseStudies from './pages/CaseStudies'
import ROICalculator from './pages/ROICalculator'
import FleetImpact from './pages/FleetImpact'
import EASCalculator from './pages/EASCalculator'
import Comparison from './pages/Comparison'
import DataExplorer from './pages/DataExplorer'
import AIAssistant from './pages/AIAssistant'
import Education from './pages/Education'
import About from './pages/About'
import LongevityAnalysis from './pages/analysis/LongevityAnalysis'
import EASPrintView from './components/print/EASPrintView'
import EquipmentAnalysis from './pages/analysis/EquipmentAnalysis'
import EngineeringAnalysis from './pages/analysis/EngineeringAnalysis'
import TrendsAnalysis from './pages/analysis/TrendsAnalysis'

// Admin pages - DEVELOPMENT ONLY
// These are local development tools that require the admin-api backend on localhost:8000
// See ADMIN_PANEL.md for documentation
// Using lazy loading so admin code is NOT bundled in production builds
const AdminLayout = import.meta.env.DEV ? lazy(() => import('./pages/admin').then(m => ({ default: m.AdminLayout }))) : null
const AdminDashboard = import.meta.env.DEV ? lazy(() => import('./pages/admin').then(m => ({ default: m.AdminDashboard }))) : null
const AdminUpload = import.meta.env.DEV ? lazy(() => import('./pages/admin').then(m => ({ default: m.Upload }))) : null
const AdminProcess = import.meta.env.DEV ? lazy(() => import('./pages/admin').then(m => ({ default: m.Process }))) : null
const AdminDeploy = import.meta.env.DEV ? lazy(() => import('./pages/admin').then(m => ({ default: m.Deploy }))) : null
const AdminLogs = import.meta.env.DEV ? lazy(() => import('./pages/admin').then(m => ({ default: m.Logs }))) : null
const AdminCustomers = import.meta.env.DEV ? lazy(() => import('./pages/admin').then(m => ({ default: m.Customers }))) : null
const AdminTrainingData = import.meta.env.DEV ? lazy(() => import('./pages/admin').then(m => ({ default: m.TrainingData }))) : null
import { CorpusDataProvider } from './context/CorpusDataContext'
import { ComparisonProvider } from './context/ComparisonContext'
import { FilterProvider } from './context/FilterContext'
import { ToastProvider } from './context/ToastContext'
import { KeyboardShortcutsProvider } from './context/KeyboardShortcutsContext'
import GlossaryPanel from './components/ui/GlossaryPanel'
import ScrollToTop from './components/ui/ScrollToTop'
import OnboardingTour from './components/ui/OnboardingTour'

function App() {
  return (
    <ThemeProvider>
    <ToastProvider>
    <CorpusDataProvider>
    <FilterProvider>
    <ComparisonProvider>
    <KeyboardShortcutsProvider>
      <Routes>
        {/* Landing page - no sidebar */}
        <Route path="/" element={<Landing />} />

        {/* Print view - standalone, no layout */}
        <Route path="/eas-print" element={<EASPrintView />} />

        {/* Main app with sidebar layout */}
        <Route element={<PageLayout><ScrollToTop /><GlossaryPanel /><OnboardingTour /></PageLayout>}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/case-studies" element={<CaseStudies />} />
          <Route path="/roi-calculator" element={<ROICalculator />} />
          <Route path="/fleet-impact" element={<FleetImpact />} />
          <Route path="/eas-calculator" element={<EASCalculator />} />
          <Route path="/compare" element={<Comparison />} />
          <Route path="/analysis" element={<Navigate to="/analysis/longevity" replace />} />
          <Route path="/analysis/longevity" element={<LongevityAnalysis />} />
          <Route path="/analysis/equipment" element={<EquipmentAnalysis />} />
          <Route path="/analysis/engineering" element={<EngineeringAnalysis />} />
          <Route path="/analysis/trends" element={<TrendsAnalysis />} />
          <Route path="/data-explorer" element={<DataExplorer />} />
          <Route path="/education" element={<Education />} />
          <Route path="/ai-assistant" element={<AIAssistant />} />
          <Route path="/about" element={<About />} />
        </Route>

        {/* Admin Routes - Development only */}
        {/* These routes are excluded from production builds */}
        {/* Admin panel requires localhost:8000 backend - see ADMIN_PANEL.md */}
        {import.meta.env.DEV && AdminLayout && (
          <Route path="/admin" element={
            <Suspense fallback={<div style={{ padding: 20 }}>Loading admin...</div>}>
              <AdminLayout />
            </Suspense>
          }>
            <Route index element={<Suspense fallback={null}><AdminDashboard /></Suspense>} />
            <Route path="upload" element={<Suspense fallback={null}><AdminUpload /></Suspense>} />
            <Route path="process" element={<Suspense fallback={null}><AdminProcess /></Suspense>} />
            <Route path="deploy" element={<Suspense fallback={null}><AdminDeploy /></Suspense>} />
            <Route path="logs" element={<Suspense fallback={null}><AdminLogs /></Suspense>} />
            <Route path="customers" element={<Suspense fallback={null}><AdminCustomers /></Suspense>} />
            <Route path="training-data" element={<Suspense fallback={null}><AdminTrainingData /></Suspense>} />
          </Route>
        )}
      </Routes>
    </KeyboardShortcutsProvider>
    </ComparisonProvider>
    </FilterProvider>
    </CorpusDataProvider>
    </ToastProvider>
    </ThemeProvider>
  )
}

export default App
